import Notice from './Notice.jsx';
import SectionCard from './SectionCard.jsx';

// Page states (ENTRAID-347 redesign, brand/onguard/README.md "States"):
//   loading  a skeleton of the page's own layout (a header plus a row of grey cards), never "Loading...";
//   empty    a section card with a title, one sentence on why it is empty and when it fills, and one action;
//   error    a bad notice with the message in words, plus Retry.
// Each state is announced: loading and empty are role="status", error is role="alert".

/**
 * @param {{
 *   state: 'loading'|'empty'|'error', title?: string, message?: import('react').ReactNode,
 *   action?: import('react').ReactNode, onRetry?: () => void, cards?: number, label?: string,
 * }} props
 * `cards` is the number of skeleton KPI cards (0 for none); `header` false skips the header skeleton (the caller
 * already shows its PageHeader); `label` names what is loading for screen readers.
 */
export default function PageState({ state, title, message = null, action = null, onRetry, cards = 4, header = true, label = 'Loading' }) {
  if (state === 'loading') {
    return (
      <div role="status" aria-live="polite" data-page-state="loading">
        <span className="sr-only">{label}</span>
        <div aria-hidden="true" className="motion-safe:animate-pulse">
          {header ? (
            <div className="mb-6">
              <div className="h-8 w-56 rounded-md bg-ipc-track" />
              <div className="mt-3 h-4 w-80 max-w-full rounded bg-ipc-track" />
            </div>
          ) : null}
          {cards > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: cards }, (_, i) => (
                <div key={i} className="h-[184px] rounded-2xl border border-ipc-border bg-white p-6">
                  <div className="h-4 w-28 rounded bg-ipc-track" />
                  <div className="mt-5 h-12 w-20 rounded bg-ipc-track" />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
  if (state === 'error') {
    return (
      <Notice
        tone="bad"
        title={title ?? 'Something went wrong'}
        action={onRetry ? <button type="button" className="btn-secondary text-sm" onClick={onRetry}>Retry</button> : action}
        dataAttrs={{ 'data-page-state': 'error' }}
      >
        {message}
      </Notice>
    );
  }
  return (
    <div role="status" data-page-state="empty">
      <SectionCard title={title}>
        {message ? <p className="text-sm text-ipc-muted">{message}</p> : null}
        {action ? <div className="mt-4">{action}</div> : null}
      </SectionCard>
    </div>
  );
}
